import { ConsolePage, Icon, Panel, Segmented } from '@/components';
import { useToast } from '@/components';
import { useTheme } from '@/theme/ThemeProvider';
import { CORNER_NAMES, THEMES, THEME_NAMES, type ThemeName } from '@/theme/themes';

/**
 * Super-Admin-only. Sets the organization-wide theme + corner style for every
 * Warm Paper ED user. The choice persists (mock: localStorage; production:
 * a `settings` row). Other roles never see this screen — it's gated in the
 * router behind RequireRole min="superadmin".
 */
export function AdminAppearance() {
  const { theme, corners, setTheme, setCorners } = useTheme();
  const { toast } = useToast();

  const choose = (name: ThemeName) => {
    setTheme(name);
    toast(`Theme set to ${name} — applied org-wide`, { icon: 'check' });
  };

  return (
    <ConsolePage title="Appearance" th="ธีมและรูปลักษณ์" crumbs="SYSTEM / SUPER ADMIN" maxWidth={1040}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        <div style={{ display: 'flex', gap: 8, padding: '12px 14px', background: 'var(--sage-tint)', borderRadius: 10, fontSize: 13, color: 'var(--sage-deep)' }}>
          <Icon name="shield" size={16} style={{ flex: 'none', marginTop: 1 }} />
          <span>
            <strong>Organization-wide.</strong> The theme you pick here is applied to every Warm Paper ED user across mobile and desktop. Only a Super Admin can change it.
          </span>
        </div>

        <Panel title="Color theme · ธีมสี" action={<span className="badge badge-sage"><span className="dot" /> {theme}</span>}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(150px,1fr))', gap: 12 }}>
            {THEME_NAMES.map((name) => {
              const def = THEMES[name];
              const [accent, bg, ink] = def.swatch;
              const on = theme === name;
              return (
                <button key={name} onClick={() => choose(name)} style={{ all: 'unset', cursor: 'pointer', display: 'block' }}>
                  <div
                    className="lift"
                    style={{
                      borderRadius: 14,
                      overflow: 'hidden',
                      border: `1.5px solid ${on ? accent : 'var(--line)'}`,
                      boxShadow: on ? `0 0 0 3px color-mix(in srgb, ${accent} 22%, transparent)` : 'none',
                      background: 'var(--card)',
                    }}
                  >
                    {/* preview swatch */}
                    <div style={{ height: 62, background: bg, position: 'relative', display: 'flex', alignItems: 'center', padding: 12, gap: 8 }}>
                      <span style={{ width: 22, height: 22, borderRadius: '50%', background: accent }} />
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <span style={{ width: 46, height: 6, borderRadius: 3, background: ink, opacity: 0.85 }} />
                        <span style={{ width: 32, height: 6, borderRadius: 3, background: ink, opacity: 0.35 }} />
                      </div>
                      {on && (
                        <span style={{ position: 'absolute', top: 8, right: 8, width: 20, height: 20, borderRadius: '50%', background: accent, color: '#fff', display: 'grid', placeItems: 'center' }}>
                          <Icon name="check" size={12} stroke={3} />
                        </span>
                      )}
                    </div>
                    <div style={{ padding: '10px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid var(--line)' }}>
                      <span style={{ fontSize: 13.5, fontWeight: on ? 600 : 500 }}>{name}</span>
                      {on && <span className="eyebrow" style={{ color: 'var(--sage-deep)' }}>Active</span>}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </Panel>

        <Panel title="Corner style · มุมโค้ง">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <Segmented value={corners} onChange={setCorners} options={CORNER_NAMES.map((c) => ({ value: c, label: c }))} />
            <div style={{ display: 'flex', gap: 12 }}>
              {(['Soft', 'Calm', 'Sharp'] as const).map((c) => (
                <div key={c} style={{ flex: 1, textAlign: 'center' }}>
                  <div style={{ height: 54, background: 'var(--card-2)', border: '1px solid var(--line)', borderRadius: c === 'Soft' ? 18 : c === 'Calm' ? 12 : 4 }} />
                  <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 6 }}>{c}</div>
                </div>
              ))}
            </div>
          </div>
        </Panel>
      </div>
    </ConsolePage>
  );
}
